'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowBigUp, ArrowBigDown, ArrowLeft, MessageSquare, Pin, Lock, Unlock, Trash2, CornerDownRight, Pencil, Minus, Plus, Eye, Bookmark, Bell, BellRing, ShieldCheck } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';
import { timeAgo, voteOn, localizedTitle, localizedBody, needsPinEmoji, type ForumComment, type ForumThread } from '../lib';
import { useImageAttachments, ImagePicker, BodyContent, BodyEditor, appendImageTokens } from '../images';
import ShareMenu from '../ShareMenu';
import ReportButton from '../ReportButton';
import { AiBadge } from '../AiBadge';
import MentionTextarea from '../MentionTextarea';
import type { ThreadWithComments } from '@/lib/forum/getThread';

interface CommentNode extends ForumComment {
  children: CommentNode[];
}

type CommentSort = 'top' | 'new' | 'old';

function makeComparator(sort: CommentSort) {
  return (a: CommentNode, b: CommentNode) => {
    if (sort === 'top') {
      if (b.score !== a.score) return b.score - a.score;
      return a.createdAt.localeCompare(b.createdAt);
    }
    if (sort === 'new') return b.createdAt.localeCompare(a.createdAt);
    return a.createdAt.localeCompare(b.createdAt);
  };
}

function buildTree(comments: ForumComment[], sort: CommentSort): CommentNode[] {
  const byId = new Map<string, CommentNode>();
  comments.forEach((c) => byId.set(c.id, { ...c, children: [] }));
  const roots: CommentNode[] = [];
  byId.forEach((node) => {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const cmp = makeComparator(sort);
  const sortDeep = (nodes: CommentNode[]) => {
    nodes.sort(cmp);
    nodes.forEach((n) => sortDeep(n.children));
  };
  sortDeep(roots);
  return roots;
}

function countDescendants(node: CommentNode): number {
  return node.children.reduce((sum, c) => sum + 1 + countDescendants(c), 0);
}

const COMMENT_SORTS: Array<[CommentSort, string]> = [['top', 'Best'], ['new', 'New'], ['old', 'Oldest']];

// Client thread page. `initialData` (from the server page.tsx) seeds the thread + comments so
// the h1/body/comments are in the SSR HTML; the API refetch on mount still runs to pick up the
// viewer-specific fields (myVote, isSaved, isFollowing), lazy translations and the view count.
export default function ForumThreadPage({ id, initialData }: { id: string; initialData?: ThreadWithComments | null }) {
  const router = useRouter();
  const { user, accessToken } = useAuth();
  const { lang, t } = useLang();

  const [thread, setThread] = useState<ForumThread | null>(initialData?.thread ?? null);
  const [comments, setComments] = useState<ForumComment[]>(initialData?.comments ?? []);
  const [loading, setLoading] = useState(!initialData);
  const [notFound, setNotFound] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [commentSort, setCommentSort] = useState<CommentSort>('top');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editingThread, setEditingThread] = useState(false);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [isMod, setIsMod] = useState(false);

  useEffect(() => {
    if (!user) { setIsMod(false); return; }
    fetch('/api/forum/moderate')
      .then((r) => r.json())
      .then((d) => setIsMod(!!d.admin))
      .catch(() => {});
  }, [user]);
  // One attachment set for whichever composer is active (top-level or reply)
  const attach = useImageAttachments(accessToken);

  // Only one composer at a time: replying closes edits and vice versa
  function startReply(id: string | null) {
    setReplyTo(id);
    setCommentText('');
    setSubmitError(null);
    setEditingComment(null);
    setEditingThread(false);
    attach.reset();
  }
  function startEditComment(id: string | null) {
    setEditingComment(id);
    setReplyTo(null);
    setCommentText('');
    setSubmitError(null);
    setEditingThread(false);
    attach.reset();
  }

  function toggleCollapsed(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/forum/threads/${id}`);
      if (!res.ok) {
        // 404 = really gone; a transient error with server-seeded data keeps what we have.
        if (res.status === 404 || !initialData) setNotFound(true);
        return;
      }
      const d = await res.json();
      setThread(d.thread);
      setComments(d.comments ?? []);
    } catch {
      if (!initialData) setNotFound(true);
    } finally {
      setLoading(false);
    }
    // initialData is only read for the "keep what we have" fallback; it never changes for a mounted page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleThreadVote(dir: 1 | -1) {
    if (!user || !thread) { if (!user) router.push('/sign-up-login-screen'); return; }
    const next = thread.myVote === dir ? 0 : dir;
    setThread({ ...thread, myVote: next, score: thread.score - thread.myVote + next });
    const ok = await voteOn('THREAD', thread.id, next as 1 | -1 | 0);
    if (!ok) load();
  }

  async function handleCommentVote(comment: ForumComment, dir: 1 | -1) {
    if (!user) { router.push('/sign-up-login-screen'); return; }
    const next = comment.myVote === dir ? 0 : dir;
    setComments((prev) =>
      prev.map((c) => (c.id === comment.id ? { ...c, myVote: next, score: c.score - comment.myVote + next } : c))
    );
    const ok = await voteOn('COMMENT', comment.id, next as 1 | -1 | 0);
    if (!ok) load();
  }

  async function handleSubmitComment() {
    if (!user) { router.push('/sign-up-login-screen'); return; }
    const body = appendImageTokens(commentText.trim(), attach.images);
    if (!body || !thread) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/forum/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadId: thread.id, body, parentId: replyTo }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setComments((prev) => [...prev, { ...d.comment, myVote: 0 }]);
        setThread((th) => (th ? { ...th, commentCount: th.commentCount + 1 } : th));
        setCommentText('');
        setReplyTo(null);
        attach.reset();
      } else {
        setSubmitError(d.error || t('Something went wrong'));
      }
    } catch {
      setSubmitError(t('Something went wrong'));
    } finally {
      setSubmitting(false);
    }
  }

  async function saveThreadEdit(body: string): Promise<string | null> {
    if (!thread) return null;
    try {
      const res = await fetch(`/api/forum/threads/${thread.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) return d.error || t('Something went wrong');
      setThread((th) => (th ? { ...th, body: d.thread.body, editedAt: d.thread.editedAt } : th));
      setEditingThread(false);
      return null;
    } catch {
      return t('Something went wrong');
    }
  }

  async function saveCommentEdit(commentId: string, body: string): Promise<string | null> {
    try {
      const res = await fetch('/api/forum/comments', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: commentId, body }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) return d.error || t('Something went wrong');
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, body: d.comment.body, editedAt: d.comment.editedAt } : c))
      );
      setEditingComment(null);
      return null;
    } catch {
      return t('Something went wrong');
    }
  }

  async function handleDeleteComment(commentId: string) {
    const res = await fetch(`/api/forum/comments?id=${commentId}`, { method: 'DELETE' });
    if (res.ok) {
      setComments((prev) => prev.map((c) => (c.id === commentId ? { ...c, isDeleted: true, body: '' } : c)));
    }
  }

  async function toggleSaved() {
    if (!user || !thread) { if (!user) router.push('/sign-up-login-screen'); return; }
    const next = !thread.isSaved;
    setThread({ ...thread, isSaved: next }); // optimistic
    const ok = next
      ? (await fetch('/api/forum/saved', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ threadId: thread.id }),
        })).ok
      : (await fetch(`/api/forum/saved?threadId=${thread.id}`, { method: 'DELETE' })).ok;
    if (!ok) setThread((th) => (th ? { ...th, isSaved: !next } : th));
  }

  async function toggleFollowing() {
    if (!user || !thread) { if (!user) router.push('/sign-up-login-screen'); return; }
    const next = !thread.isFollowing;
    setThread({ ...thread, isFollowing: next }); // optimistic
    const ok = next
      ? (await fetch('/api/forum/follow', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ threadId: thread.id }),
        })).ok
      : (await fetch(`/api/forum/follow?threadId=${thread.id}`, { method: 'DELETE' })).ok;
    if (!ok) setThread((th) => (th ? { ...th, isFollowing: !next } : th));
  }

  async function moderate(action: 'pin' | 'unpin' | 'lock' | 'unlock' | 'delete') {
    if (!thread) return;
    if (action === 'delete' && !window.confirm(t('Delete this topic? This cannot be undone.'))) return;
    const res = await fetch('/api/forum/moderate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ threadId: thread.id, action }),
    });
    if (!res.ok) return;
    if (action === 'delete') { router.push('/forum'); return; }
    setThread((th) =>
      th
        ? {
            ...th,
            isPinned: action === 'pin' ? true : action === 'unpin' ? false : th.isPinned,
            isLocked: action === 'lock' ? true : action === 'unlock' ? false : th.isLocked,
          }
        : th
    );
  }

  async function handleDeleteThread() {
    if (!thread) return;
    if (!window.confirm(t('Delete this topic? This cannot be undone.'))) return;
    const res = await fetch(`/api/forum/threads/${thread.id}`, { method: 'DELETE' });
    if (res.ok) router.push('/forum');
  }

  // Highest-scored live comment gets the "top contribution" pill — a simple,
  // purely-visual heuristic (no new data/schema) matching the design's TOP DOPRINOS badge.
  const topCommentId = useMemo(() => {
    let best: ForumComment | null = null;
    for (const c of comments) {
      if (c.isDeleted || c.score <= 0) continue;
      if (!best || c.score > best.score) best = c;
    }
    return best?.id ?? null;
  }, [comments]);

  function renderComment(node: CommentNode, depth: number) {
    const isCollapsed = collapsed.has(node.id);
    const hiddenCount = isCollapsed ? countDescendants(node) : 0;
    const isOp = !!thread && node.userId === thread.userId;
    const isTop = node.id === topCommentId;

    const body = (
      <>
        <div className="flex items-center gap-2 text-xs mb-1.5 flex-wrap" style={{ color: 'var(--text-muted)' }}>
          {node.children.length > 0 && (
            <button
              onClick={() => toggleCollapsed(node.id)}
              aria-label={isCollapsed ? t('Expand replies') : t('Collapse replies')}
              className="p-0.5 rounded"
              style={{ color: 'var(--text-secondary)', border: '1px solid var(--border)', lineHeight: 0 }}
            >
              {isCollapsed ? <Plus size={11} /> : <Minus size={11} />}
            </button>
          )}
          <span className="font-bold" style={{ color: 'var(--text-primary)' }}>@{node.User?.username || 'trader'}</span>
          <AiBadge username={node.User?.username} />
          {isOp && (
            <span
              className="font-extrabold rounded"
              style={{ background: 'var(--accent-muted)', color: 'var(--accent)', fontSize: 10, padding: '2px 6px' }}
            >
              {t('OP')}
            </span>
          )}
          {isTop && (
            <span
              className="font-extrabold rounded"
              style={{ background: 'var(--win-muted)', color: 'var(--win)', fontSize: 10, padding: '2px 6px' }}
            >
              {t('TOP CONTRIBUTION')}
            </span>
          )}
          <span>·</span>
          <span suppressHydrationWarning>{timeAgo(node.createdAt, lang)}</span>
          {node.editedAt && !node.isDeleted && (
            <span className="italic" title={timeAgo(node.editedAt, lang)}>({t('edited')})</span>
          )}
          {isCollapsed && hiddenCount > 0 && (
            <span style={{ color: 'var(--text-muted)' }}>· {hiddenCount} {t('hidden')}</span>
          )}
        </div>
        {editingComment === node.id ? (
          <BodyEditor
            initialBody={node.body}
            accessToken={accessToken}
            onCancel={() => setEditingComment(null)}
            onSubmit={(body) => saveCommentEdit(node.id, body)}
            submitLabel={t('Save')}
            rows={3}
            allowEmptyText={false}
          />
        ) : node.isDeleted ? (
          <p className="text-sm italic" style={{ color: 'var(--text-muted)' }}>[{t('deleted')}]</p>
        ) : isCollapsed ? null : (
          <BodyContent body={localizedBody(node, lang) ?? ''} className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--text-body)' }} />
        )}
        {!isCollapsed && editingComment !== node.id && (
        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleCommentVote(node, 1)}
              className="p-0.5"
              aria-label={t('Upvote')}
              style={{ color: node.myVote === 1 ? 'var(--bullish)' : 'var(--text-muted)' }}
            >
              <ArrowBigUp size={16} fill={node.myVote === 1 ? 'var(--bullish)' : 'none'} />
            </button>
            <span className="font-mono-nums text-xs font-bold" style={{ color: node.score > 0 ? 'var(--bullish)' : node.score < 0 ? 'var(--bearish)' : 'var(--text-secondary)' }}>
              {node.score}
            </span>
            <button
              onClick={() => handleCommentVote(node, -1)}
              className="p-0.5"
              aria-label={t('Downvote')}
              style={{ color: node.myVote === -1 ? 'var(--bearish)' : 'var(--text-muted)' }}
            >
              <ArrowBigDown size={16} fill={node.myVote === -1 ? 'var(--bearish)' : 'none'} />
            </button>
          </div>
          {!thread?.isLocked && !node.isDeleted && (
            <button
              onClick={() => startReply(replyTo === node.id ? null : node.id)}
              className="flex items-center gap-1 text-xs font-bold"
              style={{ color: replyTo === node.id ? 'var(--accent)' : 'var(--text-secondary)' }}
            >
              <CornerDownRight size={12} />
              {t('Reply')}
            </button>
          )}
          {user && node.userId === user.id && !node.isDeleted && !thread?.isLocked && (
            <button
              onClick={() => startEditComment(node.id)}
              className="flex items-center gap-1 text-xs font-semibold"
              style={{ color: 'var(--text-secondary)' }}
            >
              <Pencil size={12} />
              {t('Edit')}
            </button>
          )}
          {user && node.userId === user.id && !node.isDeleted && (
            <button
              onClick={() => handleDeleteComment(node.id)}
              className="flex items-center gap-1 text-xs font-semibold"
              style={{ color: 'var(--text-secondary)' }}
            >
              <Trash2 size={12} />
              {t('Delete')}
            </button>
          )}
          {user && node.userId !== user.id && !node.isDeleted && (
            <ReportButton targetType="COMMENT" targetId={node.id} />
          )}
        </div>
        )}

        {!isCollapsed && replyTo === node.id && (
          <div className="mt-2">
            <MentionTextarea
              value={commentText}
              onValueChange={setCommentText}
              rows={2}
              autoFocus
              placeholder={t('Write a reply…')}
              className="input-base w-full px-3 py-2 text-sm resize-y"
              onPaste={attach.onPaste}
              onDrop={attach.onDrop}
              onDragOver={attach.onDragOver}
            />
            <ImagePicker attach={attach} />
            {submitError && <p className="text-xs mt-1.5" style={{ color: 'var(--bearish)' }}>{t(submitError)}</p>}
            <div className="flex justify-end gap-2 mt-1.5">
              <button onClick={() => startReply(null)} className="btn-ghost px-3 py-1.5 text-xs">{t('Cancel')}</button>
              <button
                onClick={handleSubmitComment}
                disabled={submitting || attach.uploading || (!commentText.trim() && attach.images.length === 0)}
                className="btn-primary px-3 py-1.5 text-xs"
                style={{ opacity: submitting || attach.uploading ? 0.6 : 1 }}
              >
                {t('Reply')}
              </button>
            </div>
          </div>
        )}
      </>
    );

    // Reddit-style indent: only the root comment gets a full card; every nested
    // level is a border-left + padding-left rail inside it (per the 1e mockup).
    const childrenBlock = !isCollapsed && node.children.length > 0 && (
      <div
        className="flex flex-col gap-3 mt-3"
        style={{ borderLeft: '2.5px solid var(--border)', paddingLeft: 16 }}
      >
        {node.children.map((child) => renderComment(child, depth + 1))}
      </div>
    );

    if (depth === 0) {
      return (
        <div key={node.id} className="rounded-2xl p-5 mb-3" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          {body}
          {childrenBlock}
        </div>
      );
    }
    return (
      <div key={node.id}>
        {body}
        {childrenBlock}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="h-40 rounded-2xl animate-pulse" style={{ background: 'var(--bg-sunken)' }} />
        </div>
      </div>
    );
  }

  if (notFound || !thread) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('Topic not found')}</p>
          <Link href="/forum" className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 text-sm mt-4">
            <ArrowLeft size={14} />
            {t('Back to forum')}
          </Link>
        </div>
      </div>
    );
  }

  const tree = buildTree(comments, commentSort);
  const isAuthor = !!user && thread.userId === user.id;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-6 pb-24 md:pb-8">
        <Link href="/forum" className="inline-flex items-center gap-1.5 text-sm mb-4 font-semibold" style={{ color: 'var(--text-secondary)' }}>
          <ArrowLeft size={15} />
          {t('Back to forum')}
        </Link>

        {/* Thread card — same radius/border/padding language as the forum-list cards */}
        <div className="flex mb-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '18px 20px' }}>
          <div className="flex flex-col items-center gap-0.5 shrink-0">
            <button onClick={() => handleThreadVote(1)} className="p-0.5" aria-label={t('Upvote')} style={{ color: thread.myVote === 1 ? 'var(--bullish)' : 'var(--text-muted)' }}>
              <ArrowBigUp size={22} fill={thread.myVote === 1 ? 'var(--bullish)' : 'none'} />
            </button>
            <span className="font-mono-nums" style={{ fontSize: 13.5, fontWeight: 800, color: thread.score > 0 ? 'var(--bullish)' : thread.score < 0 ? 'var(--bearish)' : 'var(--text-secondary)' }}>
              {thread.score}
            </span>
            <button onClick={() => handleThreadVote(-1)} className="p-0.5" aria-label={t('Downvote')} style={{ color: thread.myVote === -1 ? 'var(--bearish)' : 'var(--text-muted)' }}>
              <ArrowBigDown size={22} fill={thread.myVote === -1 ? 'var(--bearish)' : 'none'} />
            </button>
          </div>

          <div className="flex-1 min-w-0 pl-4">
            <div className="flex items-center gap-2 flex-wrap mb-1.5" style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {thread.isPinned && <Pin size={12} style={{ color: 'var(--win)' }} />}
              {thread.isLocked && <Lock size={12} />}
              <span className="font-bold" style={{ color: 'var(--accent)' }}>
                {thread.ForumCategory?.emoji} {thread.ForumCategory ? t(thread.ForumCategory.name) : ''}
              </span>
              <span>·</span>
              <span>@{thread.User?.username || 'trader'}</span>
              <AiBadge username={thread.User?.username} />
              <span>·</span>
              <span suppressHydrationWarning>{timeAgo(thread.createdAt, lang)}</span>
              {thread.editedAt && (
                <span className="italic" title={timeAgo(thread.editedAt, lang)}>({t('edited')})</span>
              )}
              {thread.ticker && (
                <>
                  <span>·</span>
                  <span className="font-mono-nums font-semibold" style={{ color: 'var(--info)' }}>{thread.ticker}</span>
                </>
              )}
            </div>
            {/* Per-content DB translation (Sr/En columns) so real user posts — not just fixed UI
                strings — follow the UI language; falls back to the original until it lands.
                Body carries the visual weight; the title sits above it as a muted label. */}
            <h1 className="font-heading text-2xl font-extrabold leading-tight mb-2" style={{ color: 'var(--text-primary)' }}>
              {needsPinEmoji(thread, localizedTitle(thread, lang)) && <span aria-hidden>📌 </span>}
              {localizedTitle(thread, lang)}
            </h1>
            {editingThread ? (
              <BodyEditor
                initialBody={thread.body}
                accessToken={accessToken}
                onCancel={() => setEditingThread(false)}
                onSubmit={saveThreadEdit}
                submitLabel={t('Save')}
                rows={5}
              />
            ) : thread.body ? (
              <BodyContent body={localizedBody(thread, lang) ?? ''} className="text-base whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--text-body)' }} />
            ) : null}
            {!editingThread && (
            <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-3 text-xs font-bold" style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
              <span className="flex items-center gap-1.5">
                <MessageSquare size={13} />
                <span className="font-mono-nums">{thread.commentCount}</span> {t('comments')}
              </span>
              <span className="flex items-center gap-1.5">
                <Eye size={13} />
                <span className="font-mono-nums">{thread.viewCount ?? 0}</span>
              </span>
              <ShareMenu title={localizedTitle(thread, lang)} />
              <button
                onClick={toggleSaved}
                className="flex items-center gap-1"
                style={{ color: thread.isSaved ? 'var(--win)' : 'var(--text-secondary)' }}
              >
                <Bookmark size={12} fill={thread.isSaved ? 'var(--win)' : 'none'} />
                {thread.isSaved ? t('Saved') : t('Save')}
              </button>
              <button
                onClick={toggleFollowing}
                className="flex items-center gap-1"
                style={{ color: thread.isFollowing ? 'var(--accent)' : 'var(--text-secondary)' }}
                title={t('Get notified about every new comment in this topic')}
              >
                {thread.isFollowing ? <BellRing size={12} /> : <Bell size={12} />}
                {thread.isFollowing ? t('Following') : t('Follow')}
              </button>
              {user && !isAuthor && <ReportButton targetType="THREAD" targetId={thread.id} />}
              {isAuthor && !thread.isLocked && (
                <button
                  onClick={() => { startReply(null); setEditingThread(true); }}
                  className="flex items-center gap-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Pencil size={12} />
                  {t('Edit')}
                </button>
              )}
              {isAuthor && (
                <button onClick={handleDeleteThread} className="flex items-center gap-1" style={{ color: 'var(--bearish)' }}>
                  <Trash2 size={12} />
                  {t('Delete topic')}
                </button>
              )}
            </div>
            )}
            {/* Moderator row (ADMIN_USER_IDS) */}
            {isMod && !editingThread && (
              <div className="flex items-center gap-3 mt-2 text-xs flex-wrap rounded-xl px-2.5 py-1.5" style={{ background: 'var(--accent-muted)', border: '1px dashed var(--accent)' }}>
                <span className="flex items-center gap-1 font-bold" style={{ color: 'var(--accent)' }}>
                  <ShieldCheck size={12} />
                  {t('Moderation')}
                </span>
                <button onClick={() => moderate(thread.isPinned ? 'unpin' : 'pin')} className="flex items-center gap-1" style={{ color: 'var(--win)' }}>
                  <Pin size={12} />
                  {thread.isPinned ? t('Unpin') : t('Pin')}
                </button>
                <button onClick={() => moderate(thread.isLocked ? 'unlock' : 'lock')} className="flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                  {thread.isLocked ? <Unlock size={12} /> : <Lock size={12} />}
                  {thread.isLocked ? t('Unlock') : t('Lock')}
                </button>
                {!isAuthor && (
                  <button onClick={() => moderate('delete')} className="flex items-center gap-1" style={{ color: 'var(--bearish)' }}>
                    <Trash2 size={12} />
                    {t('Delete topic')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Top-level composer */}
        {thread.isLocked ? (
          <div className="rounded-2xl p-4 mb-5 flex items-center gap-2 text-sm" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
            <Lock size={15} />
            {t('This topic is locked — new comments are disabled.')}
          </div>
        ) : (
          <div className="rounded-2xl p-3 mb-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            {replyTo === null ? (
              <>
                <MentionTextarea
                  value={commentText}
                  onValueChange={setCommentText}
                  rows={3}
                  placeholder={user ? t('Write a comment…') : t('Sign in to join the discussion')}
                  disabled={!user}
                  className="input-base w-full px-3 py-2 text-sm resize-y"
                  onPaste={attach.onPaste}
                  onDrop={attach.onDrop}
                  onDragOver={attach.onDragOver}
                />
                {user && <ImagePicker attach={attach} />}
                {submitError && <p className="text-xs mt-1.5" style={{ color: 'var(--bearish)' }}>{t(submitError)}</p>}
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleSubmitComment}
                    disabled={submitting || attach.uploading || (!commentText.trim() && attach.images.length === 0) || !user}
                    className="btn-primary px-4 py-2 text-sm"
                    style={{ opacity: submitting || attach.uploading || (!commentText.trim() && attach.images.length === 0) || !user ? 0.5 : 1 }}
                  >
                    {t('Comment')}
                  </button>
                </div>
              </>
            ) : (
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{t('Replying below…')}</p>
            )}
          </div>
        )}

        {/* Comment sort — plain text control (Reddit-style: best first by default) */}
        {comments.length > 1 && (
          <div className="flex items-center gap-1.5 mb-3 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
            <span>{t('Sort by')}:</span>
            {COMMENT_SORTS.map(([key, label], i) => (
              <span key={key} className="flex items-center gap-1.5">
                <button
                  onClick={() => setCommentSort(key)}
                  style={{
                    color: commentSort === key ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontWeight: commentSort === key ? 700 : 600,
                  }}
                >
                  {t(label)}
                </button>
                {i < COMMENT_SORTS.length - 1 && <span style={{ color: 'var(--text-dim)' }}>·</span>}
              </span>
            ))}
          </div>
        )}

        {/* Comments */}
        {tree.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>{t('No comments yet. Be the first!')}</p>
        ) : (
          tree.map((node) => renderComment(node, 0))
        )}
      </div>
    </div>
  );
}
